import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { Sparkles, RefreshCcw, Building2, Users, Stethoscope, HeartPulse,
  AlertOctagon, DollarSign, CalendarDays, ArrowRight, UploadCloud, Plus, Sun, Coffee, Moon, Clock, ShieldCheck, X, ListChecks, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";

const dtf = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const timeFmt = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });

const TODAY_STYLE = {
  meeting:          { dot: "#4A6B5D", label: "Meeting" },
  facility_meeting: { dot: "#8AA79A", label: "Facility visit" },
  issue:            { dot: "#C17D6E", label: "Issue due" },
  follow_up:        { dot: "#B08A55", label: "Follow-up" },
};

export default function OverviewPage() {
  const [cc, setCc] = useState(null);
  const [briefing, setBriefing] = useState("");
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [todayEvents, setTodayEvents] = useState([]);
  const [credSheet, setCredSheet] = useState(null); // "pending" | "approved" | null
  const [facilities, setFacilities] = useState([]);
  const [nps, setNps] = useState([]);
  const [cptVolume, setCptVolume] = useState(null);
  const [underVisited, setUnderVisited] = useState(null);
  const [atRisk, setAtRisk] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/command-center");
      setCc(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[overview] load failed:", e?.message || e);
    }
    try {
      const { data } = await api.get("/analytics/cpt-volume");
      setCptVolume(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[overview] cpt volume failed:", e?.message || e);
    }
    try {
      const [uv, ar] = await Promise.all([
        api.get("/analytics/under-visited-facilities"),
        api.get("/analytics/revenue-at-risk"),
      ]);
      setUnderVisited(uv.data);
      setAtRisk(ar.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[overview] analytics load failed:", e?.message || e);
    }
    try {
      const { data } = await api.get("/drchrono/status");
      setSyncStatus(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[overview] sync status failed:", e?.message || e);
    }
  }, []);

  const loadToday = useCallback(async () => {
    // Use user-local day window; the API accepts ISO which the backend parses as UTC.
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(start.getDate() + 1);
    try {
      const { data } = await api.get("/calendar", { params: { start: start.toISOString(), end: end.toISOString() } });
      setTodayEvents(data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[overview] today failed:", e?.message || e);
    }
  }, []);

  const genBrief = async () => {
    setLoadingBrief(true);
    setBriefing("");
    try {
      const { data } = await api.post("/ai/briefing");
      setBriefing(data.briefing || "");
    } catch {
      toast.error("Briefing failed");
    } finally { setLoadingBrief(false); }
  };

  useEffect(() => { load(); loadToday(); }, [load, loadToday]);

  const openCredSheet = async (status) => {
    setCredSheet(status);
    if (facilities.length === 0) {
      try {
        const [facRes, npRes] = await Promise.all([api.get("/facilities"), api.get("/nps")]);
        setFacilities(facRes.data || []);
        setNps(npRes.data || []);
      } catch {
        toast.error("Could not load list");
      }
    }
  };

  const c = cc?.counts || {};

  const now = new Date();
  const partOfDay = now.getHours() < 12 ? "morning" : now.getHours() < 17 ? "afternoon" : "evening";
  const dayName = now.toLocaleDateString(undefined, { weekday: "long" });
  const fullDate = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const greeting = `${dayName} ${partOfDay}`;

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Dialog Healthcare · Command Center</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2" data-testid="overview-greeting">{greeting}</h1>
          <p className="text-sm text-[#5C5850] mt-1.5" data-testid="overview-date">{fullDate} · where to look first today.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={genBrief} disabled={loadingBrief} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="brief-btn">
            <Sparkles className="h-4 w-4 mr-2" /> {loadingBrief ? "Drafting..." : "AI briefing"}
          </Button>
        </div>
      </div>

      {/* Counts strip — pinned at top */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <CountCard icon={Building2} label="Facilities" value={c.facilities} to="/facilities" />
        <CountCard icon={Users} label="Nurse Practitioners" value={c.nps} to="/nps" />
        <CountCard icon={Stethoscope} label="Medical Directors" value={c.medical_directors} to="/directors" />
        <CountCard icon={HeartPulse} label="CNAs" value={c.cnas} to="/cnas" />
      </div>

      {syncStatus?.connected && (syncStatus?.last_sync || syncStatus?.live_totals) && (
        <SyncHealthCard status={syncStatus} />
      )}

      {briefing && (
        <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="briefing-card">
          <div className="text-xs uppercase tracking-[0.15em] text-[#4A6B5D] flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> AI Briefing · GPT 5.6 Terra
          </div>
          <p className="mt-3 text-[15px] text-[#292723] leading-relaxed whitespace-pre-wrap">{briefing}</p>
        </div>
      )}

      <TodayBanner events={todayEvents} partOfDay={partOfDay} />

      <TodosCard />

      {cptVolume && (cptVolume.this_week.total_units > 0 || cptVolume.last_week.total_units > 0) && (
        <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="cpt-volume-card">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
              <DollarSign className="h-3.5 w-3.5"/> CPT volume — last 7 days vs prior 7 days
            </div>
            <Link to="/cpt-prices" className="text-xs text-[#4A6B5D] hover:underline">Edit prices →</Link>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {(() => {
              const tw = cptVolume.this_week; const lw = cptVolume.last_week;
              const pct = (a, b) => (b === 0 ? (a === 0 ? 0 : 100) : ((a - b) / b) * 100);
              const rev = pct(tw.total_revenue, lw.total_revenue);
              const units = pct(tw.total_units, lw.total_units);
              const tone = (n) => n > 0 ? "text-[#3B5C46]" : n < 0 ? "text-[#8A3E30]" : "text-[#8A847A]";
              const arrow = (n) => n > 0 ? "▲" : n < 0 ? "▼" : "·";
              return (
                <>
                  <div className="border border-[#E5E0D8] rounded-md p-4 bg-[#FAF8F3]">
                    <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">This week</div>
                    <div className="mt-1 flex items-baseline gap-3">
                      <div className="heading text-3xl font-bold text-[#292723]">${tw.total_revenue.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                      <div className={`text-sm font-medium ${tone(rev)}`}>{arrow(rev)} {Math.abs(rev).toFixed(0)}%</div>
                    </div>
                    <div className="mt-1 text-xs text-[#5C5850]"><span className="font-mono">{tw.visits_with_codes}</span> coded visits · <span className="font-mono">{tw.total_units}</span> units</div>
                    <div className="mt-1 text-[10px] text-[#8A847A]">{tw.from} → {tw.to}</div>
                  </div>
                  <div className="border border-[#E5E0D8] rounded-md p-4 bg-white">
                    <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Last week</div>
                    <div className="mt-1 flex items-baseline gap-3">
                      <div className="heading text-3xl font-bold text-[#5C5850]">${lw.total_revenue.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                      <div className={`text-sm font-medium ${tone(units)}`}>{arrow(units)} {Math.abs(units).toFixed(0)}% units</div>
                    </div>
                    <div className="mt-1 text-xs text-[#5C5850]"><span className="font-mono">{lw.visits_with_codes}</span> coded visits · <span className="font-mono">{lw.total_units}</span> units</div>
                    <div className="mt-1 text-[10px] text-[#8A847A]">{lw.from} → {lw.to}</div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {atRisk?.at_risk?.length > 0 && (
        <div className="mt-6 bg-white border border-[#E7C9C1] rounded-md p-5" data-testid="revenue-at-risk-card">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.06em] text-[#8A3E30] flex items-center gap-2">
              <AlertOctagon className="h-3.5 w-3.5"/> Revenue at risk · NPs with &gt;25% drop
            </div>
            <div className="text-[10px] text-[#8A847A]">
              Last 7 days vs prior 7 · America/New_York
            </div>
          </div>
          <div className="mt-3 divide-y divide-[#F2E1DC]">
            {atRisk.at_risk.slice(0, 5).map((r) => (
              <Link key={r.np_id} to={`/nps/${r.np_id}`} className="flex items-center justify-between py-2.5 hover:bg-[#FDF6F3] px-1 rounded" data-testid={`at-risk-${r.np_id}`}>
                <div>
                  <div className="text-sm font-medium text-[#292723]">{r.np_name}</div>
                  <div className="text-[11px] text-[#8A847A] mt-0.5">
                    <span className="font-mono">${r.this_week_revenue.toLocaleString(undefined,{maximumFractionDigits:0})}</span> this week ·
                    was <span className="font-mono">${r.last_week_revenue.toLocaleString(undefined,{maximumFractionDigits:0})}</span> ·
                    visits <span className="font-mono">{r.this_week_visits}</span> vs <span className="font-mono">{r.last_week_visits}</span>
                  </div>
                </div>
                <div className="text-lg font-semibold text-[#8A3E30] font-mono">{r.delta_pct}%</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {underVisited?.facilities?.length > 0 && (
        <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="under-visited-card">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5"/> Under-visited facilities · {underVisited.total_under} below monthly target
            </div>
            <Link to="/facilities" className="text-xs text-[#4A6B5D] hover:underline">All →</Link>
          </div>
          <div className="mt-3 divide-y divide-[#E5E0D8]">
            {underVisited.facilities.slice(0, 6).map((f) => (
              <div key={f.facility_id} className="py-2.5 flex items-center justify-between gap-4" data-testid={`under-visited-${f.facility_id}`}>
                <div className="min-w-0">
                  <Link to={`/facilities/${f.facility_id}`} className="text-sm font-medium text-[#292723] hover:text-[#4A6B5D]">{f.name}</Link>
                  <div className="text-[11px] text-[#8A847A] mt-0.5 flex flex-wrap gap-x-2 items-center">
                    <span>{[f.city, f.state].filter(Boolean).join(", ") || "—"}</span>
                    <span>·</span>
                    <span><span className="font-mono">{f.actual_30d}</span>/<span className="font-mono">{f.target_per_month}</span> visits ({f.pct_of_target}%)</span>
                    {f.np_name && (<><span>·</span><span>NP: <span className="text-[#292723]">{f.np_name}</span></span></>)}
                    {!f.np_id && <span className="text-[#8A3E30] font-medium">· unassigned</span>}
                  </div>
                  {f.nearest_nps?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className="text-[10px] text-[#8A847A] uppercase tracking-[0.06em]">Nearest:</span>
                      {f.nearest_nps.map((c) => (
                        <button
                          key={c.np_id}
                          onClick={async () => {
                            try {
                              await api.put(`/facilities/${f.facility_id}`, { np_id: c.np_id });
                              toast.success(`Assigned ${c.name}`);
                              load();
                            } catch { toast.error("Assign failed"); }
                          }}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-[#B7CDBB] bg-[#E9F0EA] text-[#3B5C46] hover:bg-[#DAE5DC]"
                          data-testid={`assign-${f.facility_id}-${c.np_id}`}
                        >
                          {c.name} · {c.distance_miles}mi
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-lg font-semibold text-[#8A3E30] font-mono">-{f.gap}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today needs */}
      <div className="mt-6">
        <div className="bg-white border border-[#E5E0D8] rounded-md p-5">
          <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Today needs</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
            <StatLine icon={AlertOctagon} label="Critical issues" value={c.critical_issues} to="/issues" red={c.critical_issues > 0} />
            <StatLine icon={AlertOctagon} label="Open issues" value={c.open_issues} to="/issues" />
            <StatLine icon={Stethoscope} label="Credentials pending" value={c.md_approvals_pending} orange={c.md_approvals_pending > 0} onClick={() => openCredSheet("pending")} />
            <StatLine icon={ShieldCheck} label="Credentials approved" value={c.credentials_approved} green={(c.credentials_approved || 0) > 0} onClick={() => openCredSheet("approved")} />
            <StatLine icon={DollarSign} label="Claims needing attention" value={c.claims_attention} to="/claims" red={c.claims_attention > 0} />
          </div>
        </div>
      </div>

      {/* Facility alerts + upcoming meetings */}
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E0D8] rounded-md">
          <div className="p-5 flex items-center justify-between border-b border-[#E5E0D8]">
            <div>
              <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Priority</div>
              <h2 className="heading text-lg font-semibold">Facilities that need attention</h2>
            </div>
            <Link to="/facilities" className="text-sm text-[#4A6B5D] font-medium inline-flex items-center gap-1">
              All facilities <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {(cc?.facility_alerts?.length || 0) === 0 ? (
            <div className="p-10 text-center text-[#8A847A] text-sm">
              <RefreshCcw className="h-6 w-6 mx-auto opacity-40 mb-3" />
              All facilities are operating normally.
            </div>
          ) : (
            <div className="divide-y divide-[#E5E0D8]">
              {cc.facility_alerts.map((f) => (
                <Link
                  key={f.id}
                  to={`/facilities/${f.id}`}
                  className="flex items-center justify-between px-5 py-3 hover:bg-[#F0EBE1] transition-colors"
                  data-testid={`alert-facility-${f.id}`}
                >
                  <div className="flex items-center gap-3">
                    <StatusPill status={f.status} label={f.status === "red" ? "attention" : "soon"} />
                    <span className="font-medium text-[#292723]">{f.name}</span>
                  </div>
                  <div className="text-xs text-[#5C5850] flex gap-3">
                    {f.open_issues > 0 && <span>{f.open_issues} open</span>}
                    {f.np_approval_status === "pending" && <span>MD pending</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-md">
          <div className="p-5 border-b border-[#E5E0D8] flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Calendar</div>
              <h2 className="heading text-lg font-semibold">Upcoming meetings</h2>
            </div>
            <Link
              to="/meetings?new=1"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#4A6B5D] hover:bg-[#3E594D] text-white text-xs font-medium"
              data-testid="upcoming-schedule-btn"
            >
              <Plus className="h-3.5 w-3.5" /> Schedule
            </Link>
          </div>
          {(cc?.upcoming_meetings?.length || 0) === 0 ? (
            <div className="p-8 text-center text-[#8A847A] text-sm">
              No meetings scheduled.{" "}
              <Link to="/meetings?new=1" className="text-[#4A6B5D] font-medium hover:underline" data-testid="empty-schedule-link">Schedule one</Link>.
            </div>
          ) : (
            <div className="divide-y divide-[#E5E0D8]">
              {cc.upcoming_meetings.map((m) => (
                <div key={m.id} className="px-5 py-3">
                  <div className="text-sm font-medium text-[#292723]">{m.title}</div>
                  <div className="text-xs text-[#8A847A] mt-0.5">
                    {(() => { try { return dtf.format(new Date(m.when)); } catch { return m.when; } })()}
                  </div>
                  {m.attendees && <div className="text-xs text-[#5C5850] mt-1">{m.attendees}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Sheet open={!!credSheet} onOpenChange={(v) => !v && setCredSheet(null)}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto" data-testid="cred-sheet">
          <SheetHeader>
            <SheetTitle className="heading text-2xl">
              {credSheet === "pending" ? "Credentials pending" : "Credentials approved"}
            </SheetTitle>
            <SheetDescription>
              {credSheet === "pending"
                ? "Facilities still waiting on credentialing. Click any to open it."
                : "Facilities where credentialing is fully approved."}
            </SheetDescription>
          </SheetHeader>
          <CredList
            status={credSheet}
            facilities={facilities}
            nps={nps}
            onClose={() => setCredSheet(null)}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CredList({ status, facilities, nps, onClose }) {
  if (!status) return null;
  const npById = Object.fromEntries((nps || []).map((n) => [n.id, n]));
  const rows = (facilities || []).filter((f) => f.np_approval_status === status);

  if (rows.length === 0) {
    return (
      <div className="mt-6 bg-[#F7F5F0] border border-[#E5E0D8] rounded-md p-8 text-center" data-testid="cred-sheet-empty">
        <ShieldCheck className="h-8 w-8 mx-auto text-[#8A847A] opacity-60" />
        <p className="text-sm text-[#5C5850] mt-3">
          {status === "pending" ? "No facilities are pending credentialing." : "No facilities are marked approved yet."}
        </p>
        {status === "pending" && (
          <p className="text-xs text-[#8A847A] mt-1">Set a status from any NP's Credentialing tab to see them here.</p>
        )}
      </div>
    );
  }

  const groups = {};
  for (const f of rows) {
    const npId = f.np_id || "__unassigned__";
    if (!groups[npId]) groups[npId] = [];
    groups[npId].push(f);
  }
  const orderedNpIds = Object.keys(groups).sort((a, b) => {
    if (a === "__unassigned__") return 1;
    if (b === "__unassigned__") return -1;
    return (npById[a]?.name || "").localeCompare(npById[b]?.name || "");
  });

  return (
    <div className="mt-4 space-y-4" data-testid="cred-sheet-list">
      <div className="text-xs text-[#5C5850]">
        <span className="font-mono font-semibold text-[#292723]">{rows.length}</span> {rows.length === 1 ? "facility" : "facilities"} · grouped by NP
      </div>
      {orderedNpIds.map((npId) => {
        const np = npById[npId];
        const label = np?.name || "Unassigned";
        return (
          <div key={npId} className="border border-[#E5E0D8] rounded-md overflow-hidden">
            <div className="px-4 py-2 bg-[#F7F5F0] flex items-center justify-between">
              <div className="text-sm font-medium text-[#292723]">{label}</div>
              <div className="text-[11px] text-[#8A847A] font-mono">{groups[npId].length}</div>
            </div>
            <div className="divide-y divide-[#E5E0D8]">
              {groups[npId].map((f) => (
                <Link
                  key={f.id}
                  to={`/facilities/${f.id}`}
                  onClick={onClose}
                  className="flex items-center justify-between px-4 py-2.5 hover:bg-[#FAF8F3]"
                  data-testid={`cred-sheet-fac-${f.id}`}
                >
                  <div>
                    <div className="text-sm text-[#292723]">{f.name}</div>
                    <div className="text-xs text-[#8A847A]">{[f.city, f.state].filter(Boolean).join(", ") || "—"}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[#8A847A]" />
                </Link>
              ))}
              {np && (
                <Link
                  to={`/nps/${np.id}`}
                  onClick={onClose}
                  className="flex items-center justify-between px-4 py-2 bg-[#FAF8F3] text-[11px] text-[#4A6B5D] hover:underline"
                  data-testid={`cred-sheet-open-np-${np.id}`}
                >
                  Open {np.name}'s Credentialing tab
                  <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SyncHealthCard({ status }) {
  const s = status.last_sync || {};
  const live = status.live_totals || {};
  // Prefer live totals over the sync completion record (which can be days stale
  // if recent Force Syncs hung mid-way before writing their finalization record).
  const finished = live.newest_visit_updated_at ? new Date(live.newest_visit_updated_at)
                 : s.finished_at ? new Date(s.finished_at) : null;
  const hoursAgo = finished ? Math.round((Date.now() - finished.getTime()) / 3600000) : null;
  const errCount = Object.keys(s.endpoint_errors || {}).length;
  const totalVisits = live.visits ?? s.visits_upserted ?? 0;
  const totalVisitsWithNp = live.visits_with_np ?? 0;
  const orphans = totalVisits > 0 ? Math.max(0, totalVisits - totalVisitsWithNp) : (s.orphan_visits_remaining ?? 0);
  const hasClinical = live.clinical_notes > 0 || live.medications > 0 || live.patients > 0
                    || (s.patients_upserted || 0) + (s.clinical_notes_upserted || 0) > 0;
  const autoRelinked = s.auto_relinked_orphans ?? 0;
  const state = orphans > 100 ? "yellow"
              : errCount === 0 && hoursAgo != null && hoursAgo < 30 ? "green"
              : hoursAgo != null && hoursAgo < 72 ? "yellow"
              : "red";
  const dot = { green: "bg-[#4A6B5D]", yellow: "bg-[#B08A55]", red: "bg-[#C17D6E]" }[state];
  const label = { green: "Healthy", yellow: "Warning", red: "Stale" }[state];

  return (
    <Link to="/settings" className="mt-4 block bg-white border border-[#E5E0D8] rounded-md p-4 hover:border-[#4A6B5D] transition-colors" data-testid="sync-health-card">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 min-w-[120px]">
          <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">DrChrono sync</div>
            <div className="font-semibold text-sm text-[#292723]">{label}</div>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Last sync</div>
            <div className="text-xs text-[#292723] font-medium mt-0.5">{hoursAgo != null ? `${hoursAgo}h ago` : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Visits</div>
            <div className="text-xs font-mono font-semibold text-[#292723] mt-0.5">{totalVisits.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Clinical</div>
            <div className={`text-xs font-medium mt-0.5 ${hasClinical ? "text-[#3B5C46]" : "text-[#B08A55]"}`}>{hasClinical ? "OK" : "Pending"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Orphan visits</div>
            <div className={`text-xs font-mono font-semibold mt-0.5 ${orphans > 100 ? "text-[#8A3E30]" : orphans > 0 ? "text-[#B08A55]" : "text-[#3B5C46]"}`}>{orphans.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Warnings</div>
            <div className={`text-xs font-mono font-semibold mt-0.5 ${errCount > 0 ? "text-[#8A3E30]" : "text-[#3B5C46]"}`}>{errCount}</div>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-[#8A847A]" />
      </div>
      {autoRelinked > 0 && (
        <div className="mt-2 text-[11px] text-[#3B5C46]">✓ Auto-relinked {autoRelinked} orphan visits during last sync.</div>
      )}
      {orphans > 0 && (
        <div className="mt-2 text-[11px] text-[#8A6E2A]">
          ⚠ {orphans.toLocaleString()} visits still orphan — their DrChrono doctors aren't mapped to NPs. Fix in Settings → Doctor→NP mappings.
        </div>
      )}
    </Link>
  );
}


function CountCard({ icon: Icon, label, value, to }) {
  return (
    <Link to={to} className="bg-white border border-[#E5E0D8] rounded-md p-4 hover:-translate-y-[1px] transition-transform block">
      <div className="flex items-center gap-2 text-[#5C5850]">
        <Icon className="h-4 w-4 text-[#4A6B5D]" />
        <span className="text-xs uppercase tracking-[0.06em]">{label}</span>
      </div>
      <div className="heading text-3xl font-bold mt-2 text-[#292723]">{value ?? "—"}</div>
    </Link>
  );
}

function TodosCard() {
  const [todos, setTodos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/todos");
      setTodos(data || []);
    } catch { /* silent */ }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    try { await api.put(`/todos/${t.id}`, { done: !t.done }); await load(); }
    catch { toast.error("Could not update"); }
  };

  const open = todos.filter((t) => !t.done);
  if (loaded && open.length === 0) {
    return (
      <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-5 flex items-center justify-between" data-testid="todos-empty">
        <div className="flex items-center gap-2 text-[#5C5850]">
          <ListChecks className="h-4 w-4 text-[#4A6B5D]" />
          <div>
            <div className="text-xs uppercase tracking-[0.06em]">Todos</div>
            <div className="text-sm mt-0.5">All clear — no notes to self.</div>
          </div>
        </div>
        <Link to="/todos" className="inline-flex items-center gap-1.5 text-xs text-[#4A6B5D] hover:underline">
          <Mic className="h-3.5 w-3.5" /> Add via voice
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="todos-card">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
          <ListChecks className="h-3.5 w-3.5"/> Todos <span className="font-mono">({open.length})</span>
        </div>
        <Link to="/todos" className="inline-flex items-center gap-1.5 text-xs text-[#4A6B5D] hover:underline" data-testid="todos-open-page">
          <Mic className="h-3.5 w-3.5" /> Open list
        </Link>
      </div>
      <div className="mt-3 divide-y divide-[#E5E0D8]">
        {open.slice(0, 5).map((t) => (
          <div key={t.id} className="py-2 flex items-start gap-3" data-testid={`overview-todo-${t.id}`}>
            <button
              onClick={() => toggle(t)}
              className="mt-0.5 text-[#4A6B5D] hover:text-[#3E594D]"
              aria-label="Mark done"
            >
              <span className="block h-4 w-4 rounded-full border-2 border-[#4A6B5D]"></span>
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#292723]">{t.text}</div>
              {t.source === "voice" && (
                <div className="text-[10px] text-[#8A847A] mt-0.5 inline-flex items-center gap-1"><Mic className="h-3 w-3"/> voice</div>
              )}
            </div>
          </div>
        ))}
        {open.length > 5 && (
          <Link to="/todos" className="block py-2 text-xs text-[#4A6B5D] hover:underline">
            + {open.length - 5} more
          </Link>
        )}
      </div>
    </div>
  );
}

function TodayBanner({ events, partOfDay }) {
  if (!events) return null;
  const sorted = [...events].sort((a, b) => (a.when || "").localeCompare(b.when || ""));
  const now = new Date();
  const upcoming = sorted.filter((e) => { const d = new Date(e.when); return !Number.isNaN(d.getTime()) && d >= now; });
  const past = sorted.filter((e) => { const d = new Date(e.when); return !Number.isNaN(d.getTime()) && d < now; });
  const Icon = partOfDay === "morning" ? Coffee : partOfDay === "evening" ? Moon : Sun;

  if (sorted.length === 0) {
    return (
      <div
        className="mt-6 rounded-md border border-[#E5E0D8] bg-gradient-to-r from-[#F0EBE1] to-white p-5"
        data-testid="today-banner-empty"
      >
        <div className="flex items-center gap-2 text-[#4A6B5D]">
          <Icon className="h-4 w-4" />
          <div className="text-xs uppercase tracking-[0.15em] font-semibold">Today</div>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-[#5C5850]">Nothing scheduled today — a good day to catch up on notes.</div>
          <Link
            to="/meetings?new=1"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#4A6B5D] hover:bg-[#3E594D] text-white text-xs font-medium"
            data-testid="today-schedule-link"
          >
            <Plus className="h-3.5 w-3.5" /> Schedule meeting
          </Link>
        </div>
      </div>
    );
  }

  const row = (ev) => {
    const s = TODAY_STYLE[ev.kind] || TODAY_STYLE.meeting;
    const linkTo = ev.kind === "meeting"
      ? "/meetings"
      : ev.facility_id ? `/facilities/${ev.facility_id}`
      : ev.np_id ? `/nps/${ev.np_id}`
      : "/meetings";
    return (
      <Link
        key={ev.id}
        to={linkTo}
        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[#F0EBE1] transition-colors"
        data-testid={`today-item-${ev.id}`}
      >
        <span className="text-xs font-mono text-[#5C5850] w-20 flex-shrink-0 flex items-center gap-1.5">
          <Clock className="h-3 w-3 opacity-60" />
          {timeFmt.format(new Date(ev.when))}
        </span>
        <span className="inline-block h-2 w-2 rounded-full flex-shrink-0" style={{ background: s.dot }} />
        <span className="text-[10px] uppercase tracking-[0.06em] text-[#8A847A] w-24 flex-shrink-0">{s.label}</span>
        <span className="text-sm font-medium text-[#292723] truncate">{ev.title}</span>
        {ev.attendees && <span className="text-xs text-[#8A847A] hidden md:inline truncate">· {ev.attendees}</span>}
        <ArrowRight className="h-3.5 w-3.5 text-[#8A847A] ml-auto opacity-0 group-hover:opacity-100 flex-shrink-0" />
      </Link>
    );
  };

  return (
    <div
      className="mt-6 rounded-md border border-[#4A6B5D] bg-white p-5 shadow-[0_1px_0_rgba(0,0,0,0.03)]"
      data-testid="today-banner"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[#4A6B5D]">
          <Icon className="h-4 w-4" />
          <div className="text-xs uppercase tracking-[0.15em] font-semibold">Today · {sorted.length} on the schedule</div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#8A847A]">
          {upcoming.length > 0 && <span data-testid="today-upcoming-count">{upcoming.length} upcoming</span>}
          {past.length > 0 && <span data-testid="today-past-count">{past.length} done</span>}
          <Link
            to="/meetings?new=1"
            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-[#4A6B5D] text-[#4A6B5D] hover:bg-[#F0EBE1] text-xs font-medium"
            data-testid="today-schedule-link"
          >
            <Plus className="h-3 w-3" /> Add
          </Link>
        </div>
      </div>
      <div className="mt-3 divide-y divide-[#F0EBE1]" data-testid="today-list">
        {upcoming.map(row)}
        {past.length > 0 && upcoming.length > 0 && <div className="pt-2" />}
        {past.length > 0 && (
          <details className="pt-2" data-testid="today-past-details">
            <summary className="text-[11px] uppercase tracking-[0.06em] text-[#8A847A] cursor-pointer hover:text-[#4A6B5D]">
              Earlier today ({past.length})
            </summary>
            <div className="mt-1 opacity-75">{past.map(row)}</div>
          </details>
        )}
      </div>
    </div>
  );
}

function StatLine({ icon: Icon, label, value, to, red, orange, green, onClick }) {
  const color = red ? "text-[#C17D6E]" : orange ? "text-[#8a5f1e]" : green ? "text-[#3B5C46]" : "text-[#292723]";
  const body = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-[#5C5850]">
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
      </div>
      <span className={`font-mono font-semibold ${color}`}>{value ?? 0}</span>
    </div>
  );
  if (to) return <Link to={to} className="block hover:bg-[#F7F5F0] -mx-1 px-1 py-0.5 rounded">{body}</Link>;
  if (onClick) return (
    <button type="button" onClick={onClick} className="w-full text-left block hover:bg-[#F7F5F0] -mx-1 px-1 py-0.5 rounded" data-testid={`statline-${label.toLowerCase().replace(/\s+/g, "-")}-btn`}>
      {body}
    </button>
  );
  return body;
}
